import { uniqueId } from 'lodash'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { secToHourMinute } from '../../../utils/date'
import Tips from '../common/Tips'
import { convertHexColor } from '../../../utils/color'
import LoadingSquare from '../common/LoadingSquare'

interface Props {
  list: {
    label: string
    values: {
      title: string
      value: number
      color: string
    }[]
    label_color?: string
  }[]
  chartSize: number
  max: number
  type?: string
  gap?: boolean
  ruler?: boolean
  scale?: boolean
  labelSkip?: number
  vLabelLimit?: number
  hLabelCallback?: (month: string) => void
  hLabelButtonTitle?: string
  isGraphLoading: boolean
}

const LineChart: FC<Props> = (props) => {
  const {
    list,
    chartSize,
    max,
    type,
    gap,
    ruler,
    scale,
    labelSkip,
    vLabelLimit,
    hLabelCallback,
    hLabelButtonTitle,
    isGraphLoading,
  } = props

  const V_LABEL_LIMIT = 3

  const graphRef = useRef<HTMLCanvasElement>(null)

  const [selectedLabel, setSelectedLabel] = useState<string>("")

  const [hoverItem, setHoverItem] = useState<string>("")

  const maxValue = useMemo(() => {
    const tmpMax = Math.max(...list.map((v) => v.values.map((w) => w.value)).flat(2), max)
    const rate = type === "hour" ? 3600 : 1
    const tmpAry = Math.floor(tmpMax / rate).toString().split('')
    if (tmpAry.length < 3) {
      return 100 * rate
    }
    if (Math.floor(tmpMax / rate) % Number(`1${"0".repeat(tmpAry.length - 1)}`) === 0) {
      return tmpMax
    }
    if (tmpAry[0] === "9") {
      return Number(`1${"0".repeat(tmpAry.length)}`) * rate
    }
    if (["5", "6", "7", "8", "9"].includes(tmpAry[1])) {
      return Number(`${(Number(tmpAry[0]) + 1).toString()}${"0".repeat(tmpAry.length - 1)}`) * rate
    }
    if (["0", "1", "2", "3", "4"].includes(tmpAry[1])) {
      return Number(`${(Number(tmpAry[0])).toString()}5${"0".repeat(tmpAry.length - 2)}`) * rate
    }
    return Number(`${tmpAry[0]}${(Number(tmpAry[1]) + 1).toString()}${"0".repeat(tmpAry.length - 2)}`) * rate
  }, [list, max])

  const buildUnit = (value: number) => {
    if (!value) return <></>
    if (type === "person") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          人
        </>
      )
    }
    if (type === "biz") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          biz
        </>
      )
    }
    if (type === "page") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          枚
        </>
      )
    }
    if (type === "time") {
      return (
        <span>
          {secToHourMinute(value)}
        </span>
      )
    }
    if (type === "times") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          回
        </>
      )
    }
    if (type === "percent") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          %
        </>
      )
    }
    if (type === "hour") {
      return (
        <>
          <span>
            {Math.floor(value / 3600)}
          </span>
          時間
        </>
      )
    }
    if (type === "yen") {
      return (
        <>
          ¥
          <span>
            {value.toLocaleString()}
          </span>
        </>
      )
    }
    return (
      <></>
    )
  }

  const buildLabel = (label: string, label_color?: string) => {
    const fontColor = label_color || "#666666"
    return (
      <span
        style={{ color: fontColor }}
      >
        {label}
      </span>
    )
  }

  useEffect(() => {
    const width = graphRef.current?.clientWidth || 0
    const height = graphRef.current?.clientHeight || 0
    const canvas = graphRef.current
    if (!canvas || !canvas.getContext) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    if (!maxValue) {
      ctx.clearRect(0, 0, width, height)
      return
    }
    canvas.width = width
    canvas.height = height
    list[0].values.forEach((val, num) => {
      ctx.beginPath()
      ctx.lineWidth = 2
      ctx.strokeStyle = selectedLabel === "" || selectedLabel === val.title ? val.color || "#008ffb" : convertHexColor(val.color || "#008ffb", 0.2)
      list.forEach((v, i) => {
        const tmpHeight = height - ((v.values[num].value / maxValue) * height) ? height - ((v.values[num].value / maxValue) * height) - 1 + 8 : 8
        if (v.values[num].value) {
          ctx.lineTo(
            (width / (list.length - 1)) * i,
            height > tmpHeight ? tmpHeight : height,
          )
        }
      })
      ctx.stroke()
    })
  }, [list, maxValue, selectedLabel])

  return (
    <div className="line_chart_wrap" style={{ marginTop: "80px" }}>
      <div className={`vertical ${(ruler && type) ? "ruler" : ""}`}>
        <div
          className="line_chart_area"
          style={{
            height: `${chartSize}px`,
            justifyContent: gap ? "space-evenly" : "center",
            marginTop: "32px",
          }}
          onMouseOver={() => setHoverItem("")}
          onFocus={() => setHoverItem("")}
        >
          {
            isGraphLoading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexFlow: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 2,
                  textShadow: "1px 1px 1px #ffffff",
                }}
              >
                <LoadingSquare>
                  <>
                    表示するデータを生成しています…
                  </>
                </LoadingSquare>
              </div>
            )
          }
          {
            scale && Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
              <div
                className="scale"
                style={{
                  top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                }}
                key={uniqueId("ruler")}
              />
            ))
          }
          <canvas
            ref={graphRef}
            style={{
              height: `${chartSize}px`,
              width: `calc(100% - ${100 / list.length}%)`,
              zIndex: 1,
            }}
          />
        </div>
        {
          !Math.max(...list.map((v) => v.values.map((w) => w.value)).flat(2)) && (
            <>
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: "28px",
                  color: "#999999",
                  zIndex: 1,
                }}
              >
                表示できるデータがありません
              </div>
            </>
          )
        }
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 15px)",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            textAlign: "center",
          }}
        >
          {
            !!maxValue && list.map((value, index) => (
              <div
                style={{
                  position: "relative",
                  width: `calc(100% / ${list.length || 1})`,
                }}
                key={uniqueId("line_")}
              >
                {
                  ruler ? (
                    <div
                      style={{
                        width: "1px",
                        height: "6px",
                        backgroundColor: "#CCCCCC",
                        position: "absolute",
                        top: "-15px",
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                    />
                  ) : (
                    <></>
                  )
                }
                {
                  labelSkip && (index % labelSkip) !== 0 ? (
                    <></>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        {buildLabel(value.label, value.label_color)}
                      </div>
                      {
                        hLabelCallback && (
                          <button
                            type="button"
                            onClick={() => hLabelCallback(`${value.label}`)}
                            style={{
                              border: "solid 1px #999999",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "10px",
                              backgroundColor: "#EEEEEE",
                            }}
                          >
                            {hLabelButtonTitle}
                          </button>
                        )
                      }
                    </div>
                  )
                }
                {
                  [...value.values].map((v, i) => (
                    <>
                      {
                        !!v.value && (
                          <>
                            <Tips
                              key={uniqueId()}
                              description={
                                `${v.title}${value.label}%br%${type === "hour" ? secToHourMinute(v.value) : `¥${v.value.toLocaleString()}`}`
                              }
                              className=""
                              icon={false}
                              style={{
                                position: "absolute",
                                left: "50%",
                                top: `-${Math.max((chartSize * (v.value / maxValue)) + 8, 15)}px`,
                                transform: "translate(-50%, -50%)",
                                zIndex: i + 1,
                                opacity: selectedLabel === "" || selectedLabel === v.title ? 1 : 0.2,
                                display: "flex",
                                justifyContent: "center",
                                width: "8px",
                                height: "8px",
                                padding: 0,
                              }}
                              onMouseOver={() => setHoverItem(`${v.title}${value.label}`)}
                            >
                              <>
                                <div
                                  style={{
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    backgroundColor: v.color,
                                    display: (hoverItem === "" || hoverItem === `${v.title}${value.label}`) ? "initial" : "none",
                                  }}
                                />
                              </>
                            </Tips>
                            {
                              selectedLabel === v.title && (
                                <div
                                  style={{
                                    position: "absolute",
                                    left: "50%",
                                    top: `-${(chartSize * (v.value / maxValue)) + 30}px`,
                                    transform: "translate(-50%, -50%)",
                                    color: "#666666",
                                    zIndex: 1,
                                    fontSize: "12px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {buildUnit(v.value)}
                                </div>
                              )
                            }
                          </>
                        )
                      }
                    </>
                  ))
                }
              </div>
            ))
          }
        </div>
        {
          ruler ? (
            <div className="ruler_area">
              <div
                className="horizontal_ruler_area"
                style={{
                  top: `${chartSize}px`,
                }}
              />
              {
                type ? (
                  <div className="vertical_ruler_area">
                    <div className="label_area">
                      {
                        Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
                          <React.Fragment key={uniqueId("ruler")}>
                            <div
                              className="scale"
                              style={{
                                top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                              }}
                            />
                            <div
                              className="label"
                              style={{
                                top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                                fontSize: i ? "14px" : "20px",
                                color: i ? "" : "#a5a00e",
                              }}
                            >
                              {buildUnit(Math.floor((maxValue / (vLabelLimit || V_LABEL_LIMIT)) * ((vLabelLimit || V_LABEL_LIMIT) - i)))}
                            </div>
                          </React.Fragment>
                        ))
                      }
                    </div>
                  </div>
                ) : (
                  <></>
                )
              }
            </div>
          ) : (
            <></>
          )
        }
        {
          list.length > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                position: "absolute",
                left: "50%",
                top: "-30px",
                transform: "translateX(-50%)",
                fontSize: "10px",
                color: "#666666",
              }}
            >
              {
                list[0].values.map((v) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                      opacity: selectedLabel === "" || selectedLabel === v.title ? 1 : 0.2,
                    }}
                    onMouseEnter={() => setSelectedLabel(v.title)}
                    onMouseLeave={() => setSelectedLabel("")}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: v.color,
                      }}
                    />
                    <div>
                      {v.title}
                    </div>
                  </div>
                ))
              }
            </div>
          )
        }
      </div>
    </div>
  )
}

LineChart.defaultProps = {
  type: "",
  gap: true,
  ruler: false,
  scale: false,
  labelSkip: 0,
  vLabelLimit: 3,
  hLabelCallback: undefined,
  hLabelButtonTitle: "選択",
}

export default LineChart
